import { observable } from 'mobx';
import { autobind } from 'core-decorators';
import BaseModalStore from '@src/components/modal-container/base-modal-store';
import Toast from '@src/components/toast';
import $$tracker from '@src/tracker';
import { loadRoleOptions, loadServerOptions } from '@src/services/jxsj4-role';
import { noop } from '@src/utils/common-util';

const EMPTY_ROLE_MESSAGE = '该区服下没有角色';
const LOAD_SERVERS_FAILED_MESSAGE = '区服列表加载失败，请稍后重试';
const LOAD_ROLES_FAILED_MESSAGE = '角色列表加载失败，请稍后重试';
const NO_REGISTERED_ROLE_CODE = -18106;
const NO_REGISTERED_ROLE_MESSAGE = '请先在服务器中注册账号';
/* 第一步「确定绑定」的缺失原因反馈（2026-09-14 用户需求：静默 return false 无反馈体验差） */
const SELECT_SERVER_MESSAGE = '请选择服务器';
const SELECT_ROLE_MESSAGE = '请选择角色';
const LOADING_OPTIONS_MESSAGE = '正在加载，请稍候';

function getErrorMessage(error, fallback) {
  return error && typeof error.message === 'string' && error.message ? error.message : fallback;
}

/** get_roles 失败文案：-18106 用注册引导文案，其余以后端 message / 兜底文案为准。 */
function getRolesFailureMessage(error) {
  const code = error && error.serviceResponse ? Number(error.serviceResponse.code) : NaN;
  return code === NO_REGISTERED_ROLE_CODE ? NO_REGISTERED_ROLE_MESSAGE : getErrorMessage(error, LOAD_ROLES_FAILED_MESSAGE);
}

class RoleBindingModalStore extends BaseModalStore {
  @observable step = 1;

  @observable servers = [];

  @observable roles = [];

  @observable selectedServerId = '';

  @observable selectedRoleId = '';

  @observable loadingServers = false;

  @observable loadingRoles = false;

  @observable submitting = false;

  loadServers = loadServerOptions;

  loadRoles = loadRoleOptions;

  submitBinding = noop;

  serverRequest = null;

  roleRequestVersion = 0;

  submitPromise = null;

  lifecycleVersion = 0;

  constructor(options = {}) {
    super(options);
    Object.assign(this, options);
  }

  /** @returns {Array<Object>} Value/label options required by the single-column server Picker. */
  get serverPickerOptions() {
    return this.servers.map(server => ({ value: server.serverId, label: server.serverName }));
  }

  /** @returns {Array<Object>} 仅名称与 ID；原始角色元数据不进入 Picker 契约。 */
  get rolePickerOptions() {
    return this.roles.map(role => ({ value: role.roleId, label: role.roleName }));
  }

  /**
   * 从 JXSJ4 可选区服边界启动一份全新的账号级草稿。
   * @returns {Promise<Array<Object>>} 当前生命周期的区服选项；过期/失败时为空结果。
   */
  @autobind
  openForBinding() {
    if (this.serverRequest) {
      return this.serverRequest;
    }

    this.step = 1;
    this.servers = [];
    this.roles = [];
    this.selectedServerId = '';
    this.selectedRoleId = '';
    this.loadingServers = true;
    this.roleRequestVersion += 1;

    const lifecycleVersion = this.lifecycleVersion;
    let request;

    request = Promise.resolve()
      .then(() => this.loadServers())
      .then(servers => {
        if (lifecycleVersion !== this.lifecycleVersion || this.serverRequest !== request) {
          return [];
        }
        this.servers = servers;
        return servers;
      })
      .catch(error => {
        if (lifecycleVersion === this.lifecycleVersion && this.serverRequest === request) {
          Toast.fail(getErrorMessage(error, LOAD_SERVERS_FAILED_MESSAGE));
        }
        return [];
      })
      .then(result => {
        if (lifecycleVersion === this.lifecycleVersion && this.serverRequest === request) {
          this.loadingServers = false;
          this.serverRequest = null;
        }
        return result;
      });

    this.serverRequest = request;
    return request;
  }

  /**
   * 发起新选中的 JXSJ4 区服请求前，先清空旧的角色草稿。
   * @param {string|number} serverId 区服 Picker 发出的 ID。
   * @returns {Promise<boolean>} 当前生命周期是否接受并完成了该选择。
   */
  @autobind
  selectServer(serverId) {
    const server = this.servers.find(item => item.serverId === String(serverId) && item.status === 1);
    if (!server || this.submitting) {
      return Promise.resolve(false);
    }

    this.step = 1;
    this.selectedServerId = server.serverId;
    this.selectedRoleId = '';
    this.roles = [];
    this.loadingRoles = true;
    this.roleRequestVersion += 1;
    const requestVersion = this.roleRequestVersion;
    const lifecycleVersion = this.lifecycleVersion;

    let roleRequest;
    try {
      roleRequest = this.loadRoles(server.serverId);
    } catch (error) {
      roleRequest = Promise.reject(error);
    }

    return Promise.resolve(roleRequest)
      .then(roles => {
        if (lifecycleVersion !== this.lifecycleVersion || requestVersion !== this.roleRequestVersion) {
          return false;
        }
        this.roles = roles;
        if (roles.length === 0) {
          Toast.fail(EMPTY_ROLE_MESSAGE);
        }
        return true;
      })
      .catch(error => {
        if (lifecycleVersion === this.lifecycleVersion && requestVersion === this.roleRequestVersion) {
          Toast.fail(getRolesFailureMessage(error));
        }
        return false;
      })
      .then(result => {
        if (lifecycleVersion === this.lifecycleVersion && requestVersion === this.roleRequestVersion) {
          this.loadingRoles = false;
        }
        return result;
      });
  }

  /**
   * 只接受当前区服返回的角色，过期 Picker 值无法进入确认。
   * @param {string|number} roleId 角色 Picker 发出的 ID。
   * @returns {boolean} 当前草稿是否接受了该角色。
   */
  @autobind
  selectRole(roleId) {
    const normalizedRoleId = String(roleId);
    const role = this.roles.find(item => item.roleId === normalizedRoleId && item.serverId === this.selectedServerId);
    if (!role || this.loadingRoles || this.submitting) {
      return false;
    }
    this.selectedRoleId = role.roleId;
    return true;
  }

  /**
   * 校验第一步草稿，每种拒绝都按原因 Toast
   *（2026-09-14：静默 false 曾让置灰按钮无任何反馈）。
   * @returns {boolean} 完整且未锁定的草稿是否进入纯名称的确认步骤。
   */
  @autobind
  goToConfirm() {
    if (this.loadingServers || this.loadingRoles) {
      Toast.fail(LOADING_OPTIONS_MESSAGE);
      return false;
    }

    if (!this.selectedServer) {
      Toast.fail(SELECT_SERVER_MESSAGE);
      return false;
    }

    if (!this.selectedRole) {
      Toast.fail(SELECT_ROLE_MESSAGE);
      return false;
    }

    if (this.submitting) {
      return false;
    }

    this.step = 2;
    return true;
  }

  /** @returns {boolean} 未锁定的确认是否带着完整草稿返回选择步骤。 */
  @autobind
  backToSelection() {
    if (this.submitting) {
      return false;
    }
    this.step = 1;
    return true;
  }

  /**
   * 只经 owner 回调发送 ID；公开的确认模型保持纯名称。
   * owner 必须仅在绑定真正提交后才以布尔 `true` 结算。其余任何
   * fulfilled 值、同步 throw 或 rejected Promise 在此均按可重试失败处理。
   * Toast 与绑定的持久化事实仍归页面 owner 负责。
   *
   * @returns {Promise<*>|undefined} 共享的在途结果；草稿非法时返回 undefined。
   */
  @autobind
  confirmBinding() {
    if (this.submitPromise) {
      return this.submitPromise;
    }

    const server = this.selectedServer;
    const role = this.selectedRole;
    if (this.step !== 2 || !server || !role) {
      return undefined;
    }

    $$tracker.clickConfirmRewardRole();
    this.submitting = true;
    const lifecycleVersion = this.lifecycleVersion;
    let submitResult;
    try {
      submitResult = this.submitBinding({
        serverId: server.serverId,
        serverName: server.serverName,
        roleId: role.roleId,
        roleName: role.roleName
      });
    } catch (error) {
      submitResult = Promise.reject(error);
    }

    let request;
    request = Promise.resolve(submitResult)
      .then(result => {
        if (lifecycleVersion !== this.lifecycleVersion || this.submitPromise !== request) {
          return false;
        }
        if (result === true) {
          this.onClickClose();
        }
        return result;
      }, () => undefined)
      .then(result => {
        if (lifecycleVersion === this.lifecycleVersion && this.submitPromise === request) {
          this.submitting = false;
          this.submitPromise = null;
        }
        return result;
      });

    this.submitPromise = request;
    return request;
  }

  /**
   * 使全部账号级弹窗工作失效，并绕过用户关闭的 pending 守卫。
   * 共享关闭生命周期只在挂载/打开的展示态执行，非活跃
   * store 不能移除属于其他弹窗的 body 样式。
   * 更早的异步结算观察到的是不同的生命周期代际，无法 Toast、关闭、
   * 解锁或重填下一位 owner 的草稿。
   * @returns {void}
   */
  resetForOwnerChange() {
    const shouldClose = this.isActive || this.isOpen;
    this.lifecycleVersion += 1;
    this.roleRequestVersion += 1;
    this.serverRequest = null;
    this.submitPromise = null;
    this.step = 1;
    this.servers = [];
    this.roles = [];
    this.selectedServerId = '';
    this.selectedRoleId = '';
    this.loadingServers = false;
    this.loadingRoles = false;
    this.submitting = false;
    if (shouldClose) {
      this.onClickClose();
    }
  }

  /** @returns {boolean} Whether the modal accepted the user-requested close outside submission. */
  @autobind
  requestClose() {
    if (this.submitting) {
      return false;
    }
    this.onClickClose();
    return true;
  }

  /** @returns {Object|undefined} Current selectable server, never a stale Picker ID. */
  get selectedServer() {
    return this.servers.find(server => server.serverId === this.selectedServerId && server.status === 1);
  }

  /** @returns {Object|undefined} Role belonging to the currently selected server. */
  get selectedRole() {
    return this.roles.find(role => role.roleId === this.selectedRoleId && role.serverId === this.selectedServerId);
  }

  /** @returns {{serverName: string, roleName: string}} Names-only confirmation ViewModel. */
  get confirmViewModel() {
    const server = this.selectedServer;
    const role = this.selectedRole;
    return {
      serverName: server ? server.serverName : '',
      roleName: role ? role.roleName : ''
    };
  }
}

export default RoleBindingModalStore;
